import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import {
  WebLinkTypeSelect,
  WebLinkType as WebLinkTypeNext,
} from '@/widgets/products';
import { selectProducts } from '@/entities/products';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { DataRecordEditor } from '@/shared/ui';
import { addWebLink, selectWebLinks } from '../../model/slice';
import { WebLinkType } from '../../model/types';
import { ProductSelect } from '../ProductSelect/ProductSelect';
import { URLInput } from '../URLInput/URLInput';
import { useAddWebLinkFields } from './useAddWebLinkFields';

const WEB_LINKS_LABELS_MAP: Record<WebLinkTypeNext, WebLinkType> = {
  [WebLinkTypeNext.webFunnel]: WebLinkType.webFunnel,
  [WebLinkTypeNext.web2App]: WebLinkType.web2App,
};

interface Props {
  onClose: () => void;
}

export function AddWebLinkRow({ onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const webLinks = useAppSelector(selectWebLinks);
  const [processing, setProcessing] = useState(false);
  const products = useAppSelector(selectProducts);
  const { urlInputProps, productIdProps, webLinkTypeProps } =
    useAddWebLinkFields();
  const alreadyExists = useMemo(
    () => webLinks.some((link) => link.url === urlInputProps.value.trim()),
    [webLinks, urlInputProps.value],
  );
  const hasError =
    urlInputProps.error || productIdProps.error || webLinkTypeProps.error;
  const hasRequiredValues =
    urlInputProps.value.trim() &&
    productIdProps.value &&
    webLinkTypeProps.value;

  const handleCreate = () => {
    if (
      hasError ||
      !hasRequiredValues ||
      alreadyExists ||
      webLinkTypeProps.value === ''
    ) {
      return;
    }

    setProcessing(true);
    dispatch(
      addWebLink({
        url: urlInputProps.value,
        productId: productIdProps.value,
        productName: products.find((p) => p.id === productIdProps.value)?.name!,
        type: WEB_LINKS_LABELS_MAP[webLinkTypeProps.value],
      }),
    )
      .then(onClose)
      .catch(() => {
        enqueueSnackbar('Unable to add web link. Please, try again.', {
          variant: 'error',
          autoHideDuration: 4000,
        });
        setProcessing(false);
      });
  };

  return (
    <DataRecordEditor
      sx={{ width: '100%' }}
      slotProps={{ controls: { sx: { alignItems: 'flex-start' } } }}
      title="Add Link"
      loading={processing}
      controlsSlot={
        <>
          <ProductSelect {...productIdProps} />
          <WebLinkTypeSelect {...webLinkTypeProps} />
          <URLInput {...urlInputProps} />
        </>
      }
      errorMessage={
        <>
          {hasError && (
            <Typography color="error" variant="caption">
              Please, fill values in all fields
            </Typography>
          )}
          {alreadyExists && (
            <Typography color="error" variant="caption">
              Web link already exists
            </Typography>
          )}
        </>
      }
      saveDisabled={hasError || !hasRequiredValues}
      onClose={onClose}
      onSave={handleCreate}
    />
  );
}
